import {
  RECURRENCE_INVALID_DAY_POLICIES,
  createOccurrenceKey,
  generateRecurrenceOccurrences,
  isValidRecurrenceRule,
  shiftCivilDays,
} from './recurrence';
import { isValidCivilDate } from './dates';
import { RECURRING_SEGMENT_STATUS, toDomainRecurrenceRule } from './recurrenceFirestoreSchema';

export const RECURRENCE_SPLIT_ERROR_CODES = Object.freeze({
  INVALID_INPUT: 'INVALID_RECURRENCE_SPLIT_INPUT',
  INVALID_OCCURRENCE: 'INVALID_RECURRENCE_SPLIT_OCCURRENCE',
  PAST_OVERLAP: 'RECURRENCE_SPLIT_PAST_OVERLAP',
  INVALID_UNTIL_DATE: 'RECURRENCE_SPLIT_INVALID_UNTIL_DATE',
  FUTURE_STATE_AMBIGUOUS: 'RECURRENCE_SPLIT_FUTURE_STATE_AMBIGUOUS',
  CONFLICT: 'RECURRENCE_SPLIT_CONFLICT',
});

export class RecurrenceSplitError extends Error {
  constructor(code, details = []) {
    super(code);
    this.name = 'RecurrenceSplitError';
    this.code = code;
    this.details = details;
  }
}

const fail = (code, details = []) => { throw new RecurrenceSplitError(code, details); };

export const prepareRecurringSeriesSplit = ({
  segment,
  scheduledDate,
  anchorDate,
  template,
  unit,
  interval,
  untilDate = null,
}) => {
  if (!segment || segment.status !== RECURRING_SEGMENT_STATUS.ACTIVE
    || !isValidCivilDate(scheduledDate) || !isValidCivilDate(anchorDate)
    || !template || !Number.isSafeInteger(interval) || interval < 1) {
    fail(RECURRENCE_SPLIT_ERROR_CODES.INVALID_INPUT);
  }
  const selected = generateRecurrenceOccurrences(
    toDomainRecurrenceRule(segment), scheduledDate, scheduledDate
  );
  if (selected.length !== 1
    || selected[0].occurrenceKey !== createOccurrenceKey(segment.seriesId, scheduledDate)) {
    fail(RECURRENCE_SPLIT_ERROR_CODES.INVALID_OCCURRENCE);
  }

  const history = generateRecurrenceOccurrences(
    toDomainRecurrenceRule(segment), segment.effectiveFromDate, scheduledDate
  );
  const previousOccurrence = history.at(-2)?.scheduledDate || null;
  if (previousOccurrence && anchorDate <= previousOccurrence) {
    fail(RECURRENCE_SPLIT_ERROR_CODES.PAST_OVERLAP, [previousOccurrence]);
  }
  if (untilDate && (!isValidCivilDate(untilDate) || untilDate < anchorDate)) {
    fail(RECURRENCE_SPLIT_ERROR_CODES.INVALID_UNTIL_DATE);
  }

  const nextRule = {
    seriesId: segment.seriesId,
    unit,
    interval,
    anchorDate,
    untilDate,
    effectiveFromDate: anchorDate,
    effectiveUntilDate: segment.effectiveUntilDate,
    invalidDayPolicy: RECURRENCE_INVALID_DAY_POLICIES.CLAMP,
  };
  if (!isValidRecurrenceRule(nextRule)) fail(RECURRENCE_SPLIT_ERROR_CODES.INVALID_INPUT);

  const boundary = anchorDate < scheduledDate ? anchorDate : scheduledDate;
  const previousEffectiveUntilDate = shiftCivilDays(boundary, -1);
  const cutsFirstOccurrence = previousOccurrence === null;

  return {
    seriesId: segment.seriesId,
    selectedOccurrenceKey: createOccurrenceKey(segment.seriesId, scheduledDate),
    previousOccurrence,
    previousSegment: {
      status: cutsFirstOccurrence ? RECURRING_SEGMENT_STATUS.CANCELLED : segment.status,
      effectiveUntilDate: cutsFirstOccurrence ? segment.effectiveUntilDate : previousEffectiveUntilDate,
      revision: segment.revision + 1,
    },
    nextSegment: {
      seriesId: segment.seriesId,
      status: RECURRING_SEGMENT_STATUS.ACTIVE,
      template,
      schedule: {
        unit,
        interval,
        anchorDate,
        untilDate,
        invalidDayPolicy: RECURRENCE_INVALID_DAY_POLICIES.CLAMP,
      },
      effectiveFromDate: anchorDate,
      effectiveUntilDate: segment.effectiveUntilDate,
      revision: 1,
    },
  };
};

export const isDateInSplitSchedule = (nextSegment, date) => (
  generateRecurrenceOccurrences(toDomainRecurrenceRule(nextSegment), date, date).length === 1
);
